import type React from "react";
import { useEventInstance, useYearRangeToggle } from "@/widgets/classic-form/form";
import { BirthYearCalculator } from "@/features/birth-year-calculator";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import type { SpecialFieldItem } from "@/entities/form-metadata";
import { UserActionControl } from "@/entities/form-metadata";
import { Grid } from "@/shared/components/Grid";
import styles from "./BirthYearField.module.scss";

interface YearFieldProps {
    birthYearField: SpecialFieldItem;
}

export const BirthYearField: React.FC<YearFieldProps> = ({ birthYearField }) => {
    const { fields } = birthYearField;
    const yearField = fields?.[0];

    const { instancePath, dateFieldPath, dateProximityPath, setYear, clearYear } = useEventInstance({
        eventName: "SelfBirth",
    });

    const { isRangeMode } = useYearRangeToggle(instancePath);

    if (!yearField) {
        return null;
    }

    const userActionControls = yearField.meta?.userActionControls;
    const hasCalc = userActionControls?.includes(UserActionControl.Calc);

    const setBirthYear = (year: number | null) => {
        if (year === null) {
            clearYear();
        } else {
            setYear(year);
        }
    };

    return (
        <Grid.Field spanDesktop={3} spanTablet={4} spanMobile={12}>
            <FieldComposer
                fieldPath={dateFieldPath}
                exactnessFieldPath={dateProximityPath}
                fieldConfig={fieldToConfig(yearField)}
            >
                <div className={styles.root}>
                    <div className={styles.label}>
                        <FieldComposer.Label />
                    </div>
                    <div>
                        <FieldComposer.FieldConnector>
                            {({ formField, fieldConfig, fieldPath }, id) => (
                                <FieldComposer.Inputs.Year
                                    id={id}
                                    {...formField}
                                    placeholder={yearField.label}
                                    data-testid={fieldPath}
                                    required={fieldConfig.required}
                                    onChange={setBirthYear}
                                />
                            )}
                        </FieldComposer.FieldConnector>
                        {!isRangeMode && <FieldComposer.GenericExactness />}
                    </div>
                    {hasCalc && (
                        <div className={styles.calculator}>
                            <BirthYearCalculator onSubmit={setBirthYear} />
                        </div>
                    )}
                </div>
            </FieldComposer>
        </Grid.Field>
    );
};
